// Must not import @zag/calendar/models: that package depends on this lib, so the import would form a cycle.
import { RRule } from 'rrule';
import { createRule, DAY_IN_MILLISECONDS, dateKeyToUtc, earlierDate, laterDate, utcToDateKey } from './rrule-dates';
import type { ValidityWindow } from './validity';

export interface OccurrenceWindow extends ValidityWindow {
  weekdays?: readonly number[];
}

type SharedOccurrence = { firstDate: string; weekdays: number[] };

const ALL_WEEKDAYS: readonly number[] = [0, 1, 2, 3, 4, 5, 6];
const WEEK_CYCLE_DAYS = 7;
const UNKNOWN_CYCLE_DAYS = 366;
const MIN_HORIZON_DAYS = 1096;
const CYCLE_DAYS_BY_FREQUENCY = new Map<number | undefined, number>([
  [RRule.DAILY, 1],
  [RRule.WEEKLY, 7],
  [RRule.MONTHLY, 31],
  [RRule.YEARLY, 366],
]);

export function cycleLengthDays(rrule: string | null): number {
  if (rrule === null) return WEEK_CYCLE_DAYS;
  try {
    const { freq, interval = 1 } = RRule.parseString(rrule);
    const unitDays = CYCLE_DAYS_BY_FREQUENCY.get(freq);
    return unitDays === undefined || !Number.isInteger(interval) ? UNKNOWN_CYCLE_DAYS : unitDays * interval;
  } catch {
    return UNKNOWN_CYCLE_DAYS;
  }
}

export function occurrencesBetween(window: OccurrenceWindow, from: string, to: string): string[] {
  const lower = laterDate(window.start, from);
  const upper = earlierDate(window.end, to);
  if (lower === null || upper === null || lower > upper) return [];
  const dates = window.rrule ? expandRule(window.rrule, window.start, lower, upper) : listDays(lower, upper);
  return filterWeekdays(dates, window.weekdays);
}

export function firstSharedOccurrence(a: OccurrenceWindow, b: OccurrenceWindow, notBefore: string | null = null): SharedOccurrence | null {
  const from = laterDate(laterDate(a.start, b.start), notBefore);
  const to = earlierDate(a.end, b.end);
  if (from === null || (to !== null && from > to)) return null;
  if (!a.rrule && !b.rrule) return firstSharedWeekday(from, to, sharedWeekdays(a, b));

  const horizonEnd = addDays(from, horizonDays(a, b));
  const scanEnd = to === null || to > horizonEnd ? horizonEnd : to;
  return firstCommonDate(occurrencesBetween(a, from, scanEnd), occurrencesBetween(b, from, scanEnd));
}

function expandRule(rrule: string, start: string | null, from: string, to: string): string[] {
  if (start === null) return [];
  try {
    const rangeEnd = new Date(dateKeyToUtc(to).getTime() + DAY_IN_MILLISECONDS - 1);
    return createRule(rrule, start).between(dateKeyToUtc(from), rangeEnd, true).map(utcToDateKey);
  } catch {
    return [];
  }
}

function listDays(from: string, to: string): string[] {
  const days: string[] = [];
  const last = dateKeyToUtc(to).getTime();
  for (let time = dateKeyToUtc(from).getTime(); time <= last; time += DAY_IN_MILLISECONDS) {
    days.push(utcToDateKey(new Date(time)));
  }
  return days;
}

function filterWeekdays(dates: string[], weekdays: readonly number[] | undefined): string[] {
  if (weekdays === undefined) return dates;
  return dates.filter(date => weekdays.includes(isoWeekdayOf(date)));
}

function firstCommonDate(datesA: string[], datesB: string[]): SharedOccurrence | null {
  const inB = new Set(datesB);
  const common = datesA.filter(date => inB.has(date));
  const [firstDate] = common;
  if (firstDate === undefined) return null;
  const seenWeekdays = new Set(common.map(isoWeekdayOf));
  return { firstDate, weekdays: ALL_WEEKDAYS.filter(weekday => seenWeekdays.has(weekday)) };
}

function firstSharedWeekday(from: string, to: string | null, weekdays: number[]): SharedOccurrence | null {
  const fromWeekday = isoWeekdayOf(from);
  const offsetOf = (weekday: number): number => (weekday - fromWeekday + 7) % 7;
  const span = to === null ? Infinity : daysBetween(from, to);
  const reachable = weekdays.filter(weekday => offsetOf(weekday) <= span);
  if (reachable.length === 0) return null;
  return { firstDate: addDays(from, Math.min(...reachable.map(offsetOf))), weekdays: reachable };
}

function sharedWeekdays(a: OccurrenceWindow, b: OccurrenceWindow): number[] {
  const aWeekdays = weekdaysOf(a);
  const bWeekdays = weekdaysOf(b);
  return ALL_WEEKDAYS.filter(weekday => aWeekdays.includes(weekday) && bWeekdays.includes(weekday));
}

function weekdaysOf(window: OccurrenceWindow): readonly number[] {
  return window.weekdays ?? ALL_WEEKDAYS;
}

function horizonDays(a: OccurrenceWindow, b: OccurrenceWindow): number {
  const longestCycle = Math.max(cycleLengthDays(a.rrule || null), cycleLengthDays(b.rrule || null));
  return Math.max(MIN_HORIZON_DAYS, 2 * longestCycle);
}

function isoWeekdayOf(dateKey: string): number {
  return (dateKeyToUtc(dateKey).getUTCDay() + 6) % 7;
}

function addDays(dateKey: string, days: number): string {
  return utcToDateKey(new Date(dateKeyToUtc(dateKey).getTime() + days * DAY_IN_MILLISECONDS));
}

function daysBetween(from: string, to: string): number {
  return (dateKeyToUtc(to).getTime() - dateKeyToUtc(from).getTime()) / DAY_IN_MILLISECONDS;
}
